import TimeAgo from 'javascript-time-ago'
import { createAsync, query, useParams } from "@solidjs/router";
import { createMemo, createEffect, For, Show } from "solid-js";
import { FileText } from "lucide-solid";
import { amendmentCount, certificateCost, CertificateState, padId, RIASRecordType, stageCount } from "~/lib";

const { getByUser } = await import( "~/_server/dynamoDB" );
import "~/_assets/admin.css";

const certificatesQuery = query( async ( userId: number ) => {
	"use server";

	console.log( `Getting certifier ${RIASRecordType.CERTIFICATE} for user`, userId );

	return await getByUser( RIASRecordType.CERTIFICATE, userId, -1 );
}, "getCertifierCertificates" );


const subscriptionsQuery = query( async ( userId: number ) => {
	"use server";

	console.log( `Getting certifier ${RIASRecordType.SUBSCRIPTION} for user`, userId );

	return await getByUser( RIASRecordType.SUBSCRIPTION, userId, -1 );
}, "getCertifierSubscriptions" );



export const CertifierCertificates = () => {
	const { id } = useParams();
	const timeAgo = new TimeAgo( 'en-GB' );
	const certificates = createAsync( () => certificatesQuery( parseInt( id ?? "0" ) ) );

	return (
		<table id="certificates-table" class="table-fixed w-full border-collapse text-[13px]" aria-label="Certificates list">
			<thead>
				<tr>
					<th class="text-left w-23">Status</th>
					<th class="text-left w-32">Certificate</th>
					<th class="text-left">Project</th>
					<th class="text-right w-16">Plots</th>
					<th class="text-right w-22">Fee</th>
					<th class="text-right w-26">Issued</th>
					<th class="w-10"><span class="sr-only">PDF</span></th>
				</tr>
			</thead>
			<tbody>
				<Show when={certificates()?.items?.length}>
					<For each={certificates().items}>{c => <CertificateRow c={c} timeAgo={timeAgo} />}</For>
				</Show>
			</tbody>
		</table>
	)
}


const CertificateRow = ( props: any ) => {

	const memoized = createMemo( () => {
		const isDraft = props.c.status === CertificateState.DRAFT;
		const isPaid = props.c.status === CertificateState.PAID;
		const isPublished = props.c.status === CertificateState.PUBLISHED || isPaid;
		const isStaged = !!props.c.meta.isStagedCertificate;
		const stages = stageCount( props.c );
		const amendments = amendmentCount( props.c );

		// Same id string the /certificate/[id] route parses: C0002956-Stage2-Amendment1
		let link = props.c.riasId ? `C${padId( props.c.riasId )}` : "";

		if ( link && stages ) link += `-Stage${stages}`;
		if ( link && amendments ) link += `-Amendment${amendments}`;

		// Legacy certificates carry no fee, so fall back to what the current
		// fee schedule would charge for this plot/stage/amendment combination.
		const storedFee = props.c.meta?.certificateFee ?? props.c.cost;
		const canEstimate = ["section-6-domestic", "section-6-non-domestic"].includes( props.c.scheme );
		const fee = storedFee ?? ( canEstimate ? certificateCost( props.c ) : null );

		return {
			isDraft,
			isPaid,
			isPublished,
			isStaged,
			stageCount: stages + 1,
			amendCount: amendments + 1,
			link,
			fee,
			feeEstimated: storedFee == null && fee != null,
		}
	} );

	return (
		<tr data-id="C0002956" class="certificate-row" tabindex="0" role="row" aria-selected="false">
			<td class="px-3 py-1.75 border-b border-border align-middle text-center">
				<span class={`cert-status ${props.c.status}`}>{props.c.status}</span>
			</td>
			<td class="px-3 py-1.75 border-b border-border align-middle">
				<div class="flex items-center gap-1.5">
					<span class="inline-flex items-center justify-center w-[18px] h-[18px] rounded-[4px] text-[9.5px] font-bold shrink-0 bg-blue-subtle text-blue-text" title="Domestic">D</span>
					<span class="font-mono text-[11.5px] text-text-2">C{padId( props.c.riasId )}</span>
				</div>
			</td>
			<td class="px-3 py-1.75 border-b border-border align-middle text-[13px] whitespace-nowrap overflow-hidden text-ellipsis">{props.c.name}</td>
			<td class="px-3 py-1.75 border-b border-border align-middle text-[13px] text-right">{props.c.meta.plots.length}</td>
			<td class="px-3 py-1.75 border-b border-border align-middle text-[13px] text-right">
				<Show when={memoized().fee != null} fallback="-">
					<span
						classList={{ "italic text-text-3": memoized().feeEstimated }}
						title={memoized().feeEstimated ? "Estimated from the current fee schedule - no fee stored on this certificate" : undefined}>
						{memoized().feeEstimated ? "~" : ""}£{memoized().fee}
					</span>
				</Show>
			</td>
			<td class="px-3 py-1.75 border-b border-border align-middle text-[11px] text-text-3 text-right">{props.timeAgo.format( props.c.created * 1000 )}</td>
			<td class="px-3 py-1.75 border-b border-border align-middle text-center">
				<Show when={memoized().link}>
					<a
						href={`/certificate/${memoized().link}`}
						target="_blank"
						rel="noopener noreferrer"
						title={`Open PDF ${memoized().link}`}
						class="inline-flex items-center justify-center rounded p-1 text-text-3 hover:bg-blue-subtle hover:text-blue-text">
						<FileText class="h-4 w-4" aria-hidden="true" />
						<span class="sr-only">Open PDF</span>
					</a>
				</Show>
			</td>
		</tr>
	)
}